import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from './cx.ts'

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'lg' | 'base' | 'md'
  loading?: boolean
  children: ReactNode
}

const base =
  'inline-flex cursor-pointer items-center justify-center gap-sm rounded-pill text-center font-sans text-button transition-[transform,box-shadow,background-color,border-color,filter] duration-150 ease-out-soft active:translate-y-px active:scale-[0.995] disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-selected'

/**
 * Hover/active 1:1 ze step.tutlo.com.pl (`.tk-cta`): brightness(.98) + wciśnięcie translateY(1px) scale(.995);
 * secondary = jasnofioletowa ramka jak opcje (`.tk-opt:hover`). Tailwind 4 nie daje przyciskom kursora ani hoveru domyślnie.
 */
const variants = {
  primary: 'bg-action text-action-text shadow-cta hover:brightness-[.98]',
  secondary: 'bg-bg-card text-text-primary border border-border hover:border-selected-line',
  /** Figma `Button`, Zawartość=Etykieta + ikona, Secondary (900:45): violet label on a grey outline. */
  outline: 'bg-bg-card text-selected border-[1.5px] border-border hover:border-selected-line',
  ghost: 'bg-transparent text-text-primary underline-offset-4 hover:underline',
} as const

/** Below 360 px (enlarged Android display, 35 pilot sessions) labels would break onto two lines. */
const sizes = {
  lg: 'h-[60px] w-full px-lg max-[359px]:px-base max-[359px]:text-[15px]',
  /** Hook New (863:1172): the 52 px library button with the 16 px label. */
  base: 'h-[52px] w-full px-xl text-[16px] max-[359px]:px-base max-[359px]:text-[15px]',
  md: 'h-12 px-lg',
} as const

function Spinner() {
  return (
    <svg className="size-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

/** Figma: `Button`. Żółty tylko na akcji głównej (design system Tutlo). */
export function Button({
  variant = 'primary',
  size = 'lg',
  loading = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(base, variants[variant], sizes[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : children}
    </button>
  )
}
